// lib/suggestions/channels.ts
//
// Lo único que cambia entre canales del motor es lo que entra, no las reglas:
//   - gate de catálogo: kiosko y tienda online tienen flags propios (bar/retail);
//     el display, con cajero presente, ofrece el catálogo completo (así lo hacía
//     getUpsellSuggestions, que este PR reemplaza);
//   - señales: suggestion_signals calcula 'kiosk' y 'all' (PR 2); 'online' lo
//     calcula el cron desde el PR 5c (migración 20261007200100); con menos de
//     100 pedidos del canal cae a 'all'. El display vende por POS, que no tiene
//     canal propio: usa el total.
// Puro: sin Supabase, sin React.
import type { DisplayUpsellSource, LocationFeatures } from '@/types/location-features'
import { kioskCatalogExclusions, type KioskCatalogExclusion } from '@/lib/kiosk/catalogGate'
import { onlineCatalogExclusions } from '@/lib/microsite/catalogGate'

export const SUGGESTION_CHANNELS = ['kiosk', 'display', 'online'] as const
export type SuggestionChannel = (typeof SUGGESTION_CHANNELS)[number]

// `age_verification`: con la bandera el kiosco excluye además alcohol y tabaco (caja de
// súper, Task 18). La consulta online no cambia (lo hace `hideRestrictedOnline`).
type GateFeatures = Pick<
  LocationFeatures,
  'kiosk_show_bar' | 'kiosk_show_retail' | 'online_show_bar' | 'online_show_retail' | 'age_verification'
>

/** Columnas de `items` que el canal debe excluir (`.eq(col, false)`). */
export function catalogExclusionsFor(channel: SuggestionChannel, features: GateFeatures): KioskCatalogExclusion[] {
  switch (channel) {
    case 'kiosk':
      return kioskCatalogExclusions(features)
    case 'online':
      return onlineCatalogExclusions(features)
    case 'display':
      return []
  }
}

/** Canal preferido en suggestion_signals; `pickSignalChannel` cae a 'all' si no hay filas. */
export function signalPreferenceFor(channel: SuggestionChannel): string {
  return channel === 'display' ? 'all' : channel
}

/** Valor canónico que escribe el toggle del display desde este PR, y el de apagado. */
export const DISPLAY_SOURCE_ON: DisplayUpsellSource = 'engine'
export const DISPLAY_SOURCE_OFF: DisplayUpsellSource = 'none'

/** 'none' apaga; cualquier otro valor (incluidos los viejos) enciende. */
export function isDisplaySuggestionsOn(source: DisplayUpsellSource | null | undefined): boolean {
  return source != null && source !== 'none'
}

/** Topes de la acción pública del display (entrada validada con zod). */
export const DISPLAY_SUGGESTIONS_MAX_LIMIT = 8
export const DISPLAY_CART_MAX_IDS = 100

/** Lo que la hoja de la tienda mostraba al azar: se conserva el número. */
export const ONLINE_SUGGESTIONS_LIMIT = 4

/** Toggle de la tienda online (default true en DEFAULT_FEATURES). */
export function isOnlineSuggestionsOn(features: Pick<LocationFeatures, 'online_suggestions_enabled'>): boolean {
  return features.online_suggestions_enabled !== false
}
